import { useGetAccountAnnualConsumptionMutation } from '@/services/accounts';
import { Box, Button, FormikTextInput, TextInput } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { checkValueChangeTolerance } from '../../helpers/productionValueCheck';
import { Origin } from '../../types/ProductionValueCheck';
import FormSection from '../Forms/FormSection';

import { SystemComparisonSection } from './SystemComparisonSection';
import { TwelveMonthsConsumptionInput } from './TwelveMonthsConsumptionInput';

const ENERGY_USAGE_APP_NAME = 'bemodeler' as const;

interface CommonEquipmentDetailsProps {
  isLoading: boolean;
  installed: boolean;
  installMilestoneApproved: boolean;
  firstYearContractedKwH: number;
  firstYearInstalledKwH: number;
  systemSizeInstalledKw: number;
  systemSizeContractedKw: number;
  asBuiltSystemSizeKw?: number;
  systemSizeChangeCalculator?: boolean;
}

interface EquipmentDetailsProps extends CommonEquipmentDetailsProps {
  hasCurrentContract?: boolean;
  accountId: string;
}

export const SystemSizeAndFirstYearProductionSections = ({
  isLoading,
  hasCurrentContract,
  installMilestoneApproved,
  installed,
  accountId,
  firstYearInstalledKwH,
  firstYearContractedKwH,
  systemSizeInstalledKw,
  systemSizeContractedKw,
  asBuiltSystemSizeKw,
  systemSizeChangeCalculator,
}: EquipmentDetailsProps) => {
  const { values, isSubmitting, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const navigate = useNavigate();
  const [trigger, { data: annualConsumptionData, isLoading: isConsumptionDataLoading }] =
    useGetAccountAnnualConsumptionMutation();
  const { energyUsageUi } = useFlags();

  const getSignedPercentDiff = (originalValue: number, newValue: number) => {
    const { percentDifference } = checkValueChangeTolerance({
      newValue,
      originalValue,
    });

    return newValue < originalValue ? percentDifference * -1 : percentDifference;
  };
  const firstYearDiffResults = getSignedPercentDiff(firstYearContractedKwH, firstYearInstalledKwH);
  const systemSizeDiffResults = getSignedPercentDiff(systemSizeContractedKw, systemSizeInstalledKw);

  let label1YrTotalProd: string = 'New Expected Year-1 Total Production Estimate';
  let ctrVsNew: string = 'Contracted Versus Expected Year-1 Production Difference';
  let labelSystemSize: string = 'New Expected System Size Estimate';
  let ctrVsNewSystemSize: string = 'Contracted Versus New Expected System Size Difference';
  if (installed) {
    label1YrTotalProd = 'Installed Year-1 Total Production Estimate';
    ctrVsNew = 'Contracted Versus Install Year-1 Production Difference';
    labelSystemSize = 'Installed System Size Estimate';
    ctrVsNewSystemSize = 'Contracted Versus Installed System Size Difference';
  }

  const setNumericValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.value.endsWith('.')) {
        setFieldValue(event.target.name, event.target.value);
      } else {
        const numericValue = Number(event.target.value);
        if (!isNaN(numericValue)) {
          setFieldValue(event.target.name, event.target.value);
        }
      }
    },
    [setFieldValue],
  );

  useEffect(() => {
    if (!isLoading) {
      setFieldTouched('systemFirstYearProductionKwh', true);
      setFieldTouched('systemSizeKw', true);
    }
  }, [isLoading, setFieldTouched]);

  function goToProdValueCheck() {
    navigate(`/accounts/${accountId}/production-value-check`, {
      state: {
        from: Origin.PV_SYSTEM_TAB,
      },
    });
  }

  const noOp = useCallback(() => {}, []);

  const formattedAnnualConsumptionData = useMemo(() => {
    if (!annualConsumptionData?.consumption?.length) return [];
    return annualConsumptionData.consumption
      .toSorted((a: any, b: any) => a.month - b.month)
      .map((item: any) => (item.kwh ? Number(item.kwh.toFixed(3)) : item.kwh));
  }, [annualConsumptionData]);

  const getAccountUsage = async () => {
    const annualConsumption: Array<{ month: number; consumption: number }> = values.electricityUsageKwh
      .map((consumption: number, index: number) => ({
        month: index + 1,
        consumption: consumption,
      }))
      .filter((monthRecord: { month: number; consumption: number }) => Number.isFinite(monthRecord.consumption));
    trigger({
      accountId,
      annualConsumption,
    });
  };

  useEffect(() => {
    if (formattedAnnualConsumptionData.length) {
      setFieldValue('electricityUsageKwh', formattedAnnualConsumptionData);
      setFieldValue('electricityUsageSource', ENERGY_USAGE_APP_NAME);
      setFieldValue('electricityUsageUpdated', true);
    }
  }, [formattedAnnualConsumptionData, setFieldValue]);

  const hasAtLeastOneMonthConsumptionField = useMemo(() => {
    return values?.electricityUsageKwh?.some((consumption: number) => Number.isFinite(consumption));
  }, [values.electricityUsageKwh]);

  const totalAnnualConsumption = useMemo(() => {
    if (formattedAnnualConsumptionData?.length) {
      return formattedAnnualConsumptionData.reduce((acc: number, item: number) => acc + (item || 0), 0);
    } else {
      return values.totalElectricityUsageKwh || null;
    }
  }, [formattedAnnualConsumptionData, values]);

  return (
    <>
      <FormSection title={hasCurrentContract ? 'Total Year-1 Production*' : 'Expected Total Year-1 Production'}>
        <Box
          direction={{
            base: 'column',
            tablet: 'row',
          }}
          childGap={{ base: 'lg', desktop: 'xl' }}
        >
          <SystemComparisonSection
            hasCurrentContract={hasCurrentContract}
            contractedValue={firstYearContractedKwH}
            newValue={firstYearInstalledKwH}
            percentDiff={firstYearDiffResults}
            fieldName="systemFirstYearProductionKwh"
            fieldId="systemFirstYearProductionKwh"
            suffix="kWh"
            isSubmitting={isSubmitting}
            isLoading={isLoading}
            contractedLabel={firstYearDiffResults ? 'Contracted Year-1 Production' : 'Expected Year-1 Production'}
            installedLabel={label1YrTotalProd}
            diffLabel={ctrVsNew}
            showChangeOrderButton={!installed && !installMilestoneApproved}
            changeOrderButtonLabel="Year-1 Production Value Change Order Check"
            onChangeOrderClick={goToProdValueCheck}
            systemSizeChangeCalculator={systemSizeChangeCalculator}
          />
        </Box>
      </FormSection>
      <FormSection title={hasCurrentContract ? 'DC System Size*' : 'Expected DC System Size'}>
        <Box
          direction={{
            base: 'column',
            tablet: 'row',
          }}
          childGap={{ base: 'lg', desktop: 'xl' }}
        >
          {systemSizeChangeCalculator ? (
            <SystemComparisonSection
              hasCurrentContract={hasCurrentContract}
              contractedValue={systemSizeContractedKw}
              newValue={systemSizeInstalledKw}
              percentDiff={systemSizeDiffResults}
              fieldName="systemSizeKw"
              fieldId="systemSizeKw"
              suffix="kW"
              isSubmitting={isSubmitting}
              isLoading={isLoading}
              contractedLabel={systemSizeDiffResults ? 'Contracted System Size' : 'Expected System Size'}
              installedLabel={labelSystemSize}
              diffLabel={ctrVsNewSystemSize}
              showChangeOrderButton={!installed && !installMilestoneApproved}
              changeOrderButtonLabel="System Size Change Order Calculator"
              onChangeOrderClick={goToProdValueCheck}
              systemSizeChangeCalculator={systemSizeChangeCalculator}
              onFieldChange={setNumericValue}
            />
          ) : (
            <Field
              name="systemSizeKw"
              id="systemSizeKw"
              component={FormikTextInput}
              isDisabled={isSubmitting || isLoading}
              suffix="kW"
              maxWidth="2xl"
              onChange={setNumericValue}
            />
          )}
        </Box>
      </FormSection>
      <FormSection
        title="As-Built DC System Size"
        description="System size calculated from actual installed Solar Modules."
      >
        <Box
          direction={{
            base: 'column',
            tablet: 'row',
          }}
          childGap={{ base: 'lg', desktop: 'xl' }}
        >
          <TextInput
            id="asBuiltSystemSizeKw"
            label=""
            value={asBuiltSystemSizeKw}
            onChange={noOp}
            isDisabled
            suffix="kW"
            maxWidth="2xl"
          />
        </Box>
      </FormSection>
      {energyUsageUi && (
        <FormSection
          title="Energy Usage"
          description="At least one month is required to use Palmetto Energy Calculator, or enter all 12 months of consumption"
        >
          <Box gap="md" direction="column">
            <TwelveMonthsConsumptionInput
              isLoading={isLoading}
              fieldName="electricityUsageKwh"
              min={0}
              suffix="kWh"
            />

            <Box
              direction={{
                base: 'column',
                tablet: 'row',
              }}
              childGap={{ base: 'lg', desktop: 'xl' }}
              alignItems="flex-end"
            >
              <Button
                variant="secondary"
                tone="primary"
                size="md"
                onClick={getAccountUsage}
                isDisabled={isSubmitting || isLoading || !hasAtLeastOneMonthConsumptionField}
                isLoading={isConsumptionDataLoading}
              >
                Calculate
              </Button>
            </Box>

            {totalAnnualConsumption && !isConsumptionDataLoading && (
              <Box gap="sm">
                <Box fontWeight="bold">
                  {values.electricityUsageSource === ENERGY_USAGE_APP_NAME
                    ? 'Estimated Total Annual Usage'
                    : 'Total Annual Usage'}
                </Box>
                <Box>{totalAnnualConsumption.toFixed(3)} kWh</Box>
              </Box>
            )}
          </Box>
        </FormSection>
      )}
    </>
  );
};
